/**
 * ホーム = Context Coverage。
 *
 * デモのレビューで出た指摘を 3 つ反映している。
 * - 見出しで意味を言い切る。「カバレッジ」という言葉を単体で置かない
 * - 1 画面 1 母数。数字は全部 basis の母集団のもので、別の母集団の数を並べない
 * - 未接続の注記（DataGap）は畳んで、初期表示では開かない
 */

import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "urql";
import {
  Card,
  ContextCoverageDocument,
  Disclosure,
  ErrorState,
  LoadingState,
  Meter,
  PageHeader,
  Stat,
  Tag,
  formatJstDateTime,
  type PipelineState,
  type PopulationId,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import { PopulationSelect } from "./PopulationSelect";

const STATE_TONE: Record<PipelineState, "ok" | "warn" | "neutral"> = {
  OK: "ok",
  STUCK: "warn",
  UNKNOWN: "neutral",
};

function percent(rate: number | null | undefined): string {
  return rate == null ? "—" : `${Math.round(rate * 100)} %`;
}

export function HomePage() {
  // 1 画面 1 母数。ここを切り替えると、下の数字はすべてその母数のものになる
  const [population, setPopulation] = useState<PopulationId>("DELIVERED");
  const [result, refetch] = useQuery({
    query: ContextCoverageDocument,
    variables: { population },
  });

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const coverage = result.data.contextCoverage;

  return (
    <div className="vd-page">
      <PageHeader
        title="お客さまの問いに、どれだけ答えられているか"
        description="いま答えを用意できている割合と、その内訳として何が足りていないかを出しています。"
        titleExtra={<HelpIcon slug="aio/context-coverage" />}
      />

      <div className="vd-stack">
        <Card
          title="どの商品を母数にするか"
          note="1 つの画面では 1 つの母数だけを使います。選べるのは、いま API が数えられる母集団だけです。"
        >
          <PopulationSelect value={population} onChange={setPopulation} />
        </Card>

        <Card
          title="答えられている割合"
          extra={
            <HelpIcon slug="aio/context-coverage" anchor="答えられている割合" />
          }
          note={`この画面の数字はすべて「${coverage.basis.label}」を母数にしています。${coverage.basis.note}`}
        >
          <div className="vd-row" style={{ gap: 24, alignItems: "flex-end" }}>
            <Stat
              hero
              label={coverage.basis.label}
              value={percent(coverage.answerRate)}
              note={
                coverage.basis.denominator === null
                  ? undefined
                  : `母数 ${coverage.basis.denominator.toLocaleString("ja-JP")} 件`
              }
            />
            <div style={{ flex: "1 1 240px", minWidth: 200 }}>
              <Meter ratio={coverage.answerRate ?? null} />
            </div>
          </div>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            {coverage.computedAt === null
              ? "いつ計算した数字かが分かりません。"
              : // 画面に出す時刻は日本時間の読みやすい表記にする。ISO のまま
                // 出すと、読む人が頭の中で 9 時間ずらすことになる
                `${formatJstDateTime(coverage.computedAt)} 時点のスナップショットです。`}
          </p>
        </Card>

        <Card
          title="内訳（5 工程）"
          extra={<HelpIcon slug="aio/context-coverage" anchor="内訳5-工程" />}
          note="工程ごとの数字は 1 つのスコアに合成していません。どこで止まっているかを見るためのものです。"
        >
          <div className="vd-stack">
            {coverage.stages.map((stage) => (
              <div
                key={stage.no}
                style={{
                  borderTop: "1px solid var(--vd-border)",
                  paddingTop: 12,
                }}
              >
                <div
                  className="vd-row"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="vd-card-title">
                    ({stage.no}) {stage.title}
                    <Tag tone={STATE_TONE[stage.state]}>{stage.stateLabel}</Tag>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="vd-stat-value">{stage.metric ?? "—"}</div>
                    <div className="vd-stat-note">{stage.metricNote}</div>
                  </div>
                </div>
                <p
                  style={{
                    margin: "4px 0 8px",
                    color: "var(--vd-text-secondary)",
                  }}
                >
                  {stage.summary}
                </p>
                <div className="vd-row">
                  {stage.links.map((link) => (
                    <Link key={link.route} to={link.route}>
                      {link.label}
                    </Link>
                  ))}
                </div>
                {stage.dataGap === null ? null : (
                  <Disclosure
                    summary={`「${stage.dataGap.what}」を数えていない理由`}
                  >
                    <p style={{ margin: 0 }}>{stage.dataGap.why}</p>
                    <p style={{ margin: "4px 0 0" }}>
                      必要なもの: {stage.dataGap.needs}
                      {stage.dataGap.external === null
                        ? ""
                        : `（接続先: ${stage.dataGap.external}）`}
                    </p>
                  </Disclosure>
                )}
              </div>
            ))}
          </div>
        </Card>

        <Card
          title="次のアクション"
          extra={
            <HelpIcon slug="aio/context-coverage" anchor="次のアクション" />
          }
          note="効きは「答えられるようになる問いの数」で書いています。売上や CV への寄与には読み替えていません。"
        >
          {coverage.nextActions.length === 0 ? (
            <p className="vd-stat-note" style={{ margin: 0 }}>
              いま出せる提案はありません。
            </p>
          ) : (
            <div className="vd-stack">
              {coverage.nextActions.map((action) => (
                <div key={action.id}>
                  <Link to={action.route} style={{ fontWeight: 600 }}>
                    {action.title}
                  </Link>
                  <p
                    style={{
                      margin: "2px 0 0",
                      color: "var(--vd-text-secondary)",
                    }}
                  >
                    {action.detail}
                  </p>
                  {action.expectedQuestionGain === null ? null : (
                    <p className="vd-stat-note" style={{ margin: 0 }}>
                      答えられるようになる問い: 約 {action.expectedQuestionGain}{" "}
                      件
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
